import { ChartDataset } from "chart.js";

import { postTrackingData } from "@/api/endpoints/track";
import { useI18n } from "@/contexts/i18nContext";
import { EVENT } from "@/tracking_events";
import { round } from "@/utils";
import Bar from "@components/Charts/Bar";
import ClickHandlerPlugin from "@components/Charts/plugins/ClickHandlerPlugin";
import { dynamicIntervalLabels } from "@components/Charts/utils/dynamicIntervalLabels";

import { BaseWidget } from "../BaseWidget";
import { PrValueInfoLink } from "../PRValueWidget";
import {
  PackedWidgetData,
  tooltipWithDateRangeCallback,
  unpackWidgetData,
} from "./utils";
import { WidgetProps } from "./WidgetProps";

type PRValueOverTimeData = PackedWidgetData<ChartDataset<"bar", number[]>>;

const PRValueOverTime: React.FunctionComponent<
  WidgetProps<PRValueOverTimeData>
> = (props) => {
  const { data, filters } = props;
  const { currency, formatDate, formatCurrency } = useI18n();
  const chartData = unpackWidgetData<ChartDataset<"bar">>(data);

  const hasData = data?.some(({ results }) =>
    results?.dataset?.some(({ data }) => data?.some((d: number) => d > 0))
  );

  return (
    <BaseWidget
      // The types here should be fixed within BaseWidget. That is out of scope for now.
      hasData={hasData}
      extraHeader={<PrValueInfoLink />}
      {...props}
      title={L.REPORTS_WIDGET_DEFAULT_TITLE_PR_VALUE_LINE_INSIGHTS}
    >
      {props.data && (
        <Bar
          options={{
            skipNull: true,
            plugins: {
              click: {
                onClick: (appliedFilters) => {
                  postTrackingData(EVENT.WIDGET_CLICK, {
                    widget: props.metric,
                  });
                  props.fetchMentions_v2(appliedFilters);
                },
              },
              tooltip: {
                // we're removing nullish values from this dataset. So hide the tooltip for them as well.
                filter: ({ datasetIndex, dataIndex }) => {
                  const value = chartData.datasets[datasetIndex].data[
                    dataIndex
                  ] as number;
                  return value > 0;
                },
                callbacks: {
                  label: ({ raw }) => {
                    return `${formatCurrency(round(Number(raw)))}`;
                  },

                  title: tooltipWithDateRangeCallback(
                    chartData.labels,
                    data?.[0]?.meta?.time_period,
                    formatDate,
                    filters?.end_date
                  ),
                },
              },

              legend: {
                display: false,
              },
            },
            scales: {
              x: {
                type: "timeseries",
                time: {
                  minUnit: "month",
                  tooltipFormat: "PP",
                },
                ticks: {
                  display: false,
                  source: "labels",
                },
                grid: {
                  drawTicks: true,
                },
              },
              y: {
                title: {
                  align: "center",
                  display: true,
                  text: currency,
                },
              },
              // scale to display months labels
              x2: dynamicIntervalLabels(props.data[0]?.meta?.time_period),
            },
          }}
          data={{
            ...chartData,
            datasets: chartData.datasets.map((dataset) => ({
              ...dataset,
              // return null if zero, allowing skipNull to work
              data: dataset.data.map((value) => (value === 0 ? null : value)),
              // This can technically be set on the options level... but the ts types don't allow it.
              minBarLength: 10,
            })),
          }}
          plugins={[ClickHandlerPlugin]}
        />
      )}
    </BaseWidget>
  );
};

export default PRValueOverTime;
